import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    // Use setTimeout to ensure ninja-keys element exists after body renders
    setTimeout(() => this.initialize(), 0)

    // Re-initialize on Turbo navigation to update contextual commands
    this.turboLoadHandler = () => this.reinitialize()
    document.addEventListener('turbo:load', this.turboLoadHandler)
    document.addEventListener('turbo:frame-load', this.turboLoadHandler)

    // Global keyboard listener for Cmd+/ that works in input fields
    this.keydownHandler = (e) => this.handleKeydown(e)
    document.addEventListener('keydown', this.keydownHandler, true)
  }

  disconnect() {
    document.removeEventListener('turbo:load', this.turboLoadHandler)
    document.removeEventListener('turbo:frame-load', this.turboLoadHandler)
    document.removeEventListener('keydown', this.keydownHandler, true)
  }

  handleKeydown(event) {
    // Check for Cmd+/ (Mac) or Ctrl+/ (Windows/Linux)
    if ((event.metaKey || event.ctrlKey) && event.key === '/') {
      event.preventDefault()
      event.stopPropagation()
      this.open()
    }
  }

  initialize() {
    this.ninja = document.querySelector('ninja-keys')
    if (this.ninja) {
      this.ninja.data = this.buildCommands()
      this.syncDarkMode()
    }
  }

  reinitialize() {
    // Small delay to ensure DOM is updated after navigation
    setTimeout(() => {
      if (this.ninja) {
        this.ninja.data = this.buildCommands()
        this.syncDarkMode()
      }
    }, 100)
  }

  open(event) {
    event?.preventDefault()
    event?.stopPropagation()
    const ninja = document.querySelector('ninja-keys')
    if (ninja) {
      ninja.open()
    }
  }

  syncDarkMode() {
    const isDark = document.documentElement.classList.contains('dark')
    if (isDark) {
      this.ninja.classList.add('dark')
    } else {
      this.ninja.classList.remove('dark')
    }
  }

  buildCommands() {
    const commands = [
      // Main Navigation
      {
        id: 'dashboard',
        title: 'Dashboard',
        icon: this.homeIcon(),
        section: 'Navigation',
        handler: () => { Turbo.visit('/') }
      },
      {
        id: 'customers',
        title: 'Customers',
        icon: this.usersIcon(),
        section: 'Navigation',
        handler: () => { Turbo.visit('/sales/customers') }
      },
      {
        id: 'add-website',
        title: 'Add Website',
        icon: this.plusIcon(),
        section: 'Navigation',
        handler: () => { Turbo.visit('/sales/websites/new') }
      },
      {
        id: 'settings',
        title: 'Site Settings',
        icon: this.settingsIcon(),
        section: 'Navigation',
        handler: () => {
          const modal = document.querySelector('#hs-website-settings-modal')
          if (modal && window.HSOverlay) {
            window.HSOverlay.open(modal)
          }
        }
      },

      // Sign Out
      {
        id: 'signout',
        title: 'Sign Out',
        icon: this.logoutIcon(),
        section: 'Account',
        handler: () => {
          const link = document.createElement('a')
          link.href = '/users/sign_out'
          link.setAttribute('data-turbo-method', 'delete')
          document.body.appendChild(link)
          link.click()
        }
      }
    ]

    // Add website-specific commands if we're on a website page
    const websiteId = this.getCurrentWebsiteId()
    if (websiteId) {
      commands.push(
        {
          id: 'website-details',
          title: 'Website Details',
          icon: this.websiteIcon(),
          section: 'Current Website',
          handler: () => { Turbo.visit(`/sales/websites/${websiteId}`) }
        },
        {
          id: 'website-locations',
          title: 'Store Locations',
          icon: this.mapIcon(),
          section: 'Current Website',
          handler: () => { Turbo.visit(`/sales/locations?website_id=${websiteId}`) }
        },
        {
          id: 'website-chats',
          title: 'Chats',
          icon: this.chatIcon(),
          section: 'Current Website',
          handler: () => { Turbo.visit(`/sales/chats?website_id=${websiteId}`) }
        },
        {
          id: 'website-ai-searches',
          title: 'AI Searches',
          icon: this.searchIcon(),
          section: 'Current Website',
          handler: () => { Turbo.visit(`/sales/ai_search_summaries?website_id=${websiteId}`) }
        },
        {
          id: 'website-product-summaries',
          title: 'Product Summaries',
          icon: this.documentIcon(),
          section: 'Current Website',
          handler: () => { Turbo.visit(`/sales/product_summaries?website_id=${websiteId}`) }
        },
        {
          id: 'website-products',
          title: 'Products',
          icon: this.boxIcon(),
          section: 'Current Website',
          handler: () => { Turbo.visit(`/sales/products?website_id=${websiteId}`) }
        },
        {
          id: 'website-collections',
          title: 'Collections',
          icon: this.folderIcon(),
          section: 'Current Website',
          handler: () => { Turbo.visit(`/sales/collections?website_id=${websiteId}`) }
        },
        {
          id: 'website-knowledge',
          title: 'Knowledge Base',
          icon: this.bookIcon(),
          section: 'Current Website',
          handler: () => { Turbo.visit(`/sales/website_policies?website_id=${websiteId}`) }
        }
      )
    }

    // Add websites from sidebar dynamically with nested navigation
    const websiteLinks = document.querySelectorAll('#websites-accordion-sub a[href^="/sales/websites/"]:not([href$="/new"])')
    websiteLinks.forEach((link, index) => {
      const name = link.querySelector('.truncate')?.textContent?.trim() || `Website ${index + 1}`
      const href = link.getAttribute('href')
      if (href && !href.endsWith('/new')) {
        // Extract website ID from href
        const idMatch = href.match(/\/sales\/websites\/([^\/]+)/)
        const websiteId = idMatch ? idMatch[1] : null

        if (websiteId) {
          // Parent website item
          commands.push({
            id: `website-${index}`,
            title: name,
            icon: this.websiteIcon(),
            section: 'Websites',
            keywords: name,
            children: [
              `website-${index}-settings`,
              `website-${index}-details`,
              `website-${index}-locations`,
              `website-${index}-chats`,
              `website-${index}-ai-searches`,
              `website-${index}-product-summaries`,
              `website-${index}-products`,
              `website-${index}-collections`,
              `website-${index}-knowledge`
            ],
            handler: () => {
              this.ninja.open({ parent: `website-${index}` })
              return { keepOpen: true }
            }
          })

          // Child items for website tabs
          commands.push(
            {
              id: `website-${index}-settings`,
              title: 'Site Settings',
              icon: this.settingsIcon(),
              parent: `website-${index}`,
              handler: () => {
                const currentWebsiteId = this.getCurrentWebsiteId()
                const openModal = () => {
                  const modal = document.querySelector('#hs-website-settings-modal')
                  if (modal && window.HSOverlay) {
                    window.HSOverlay.open(modal)
                  }
                }

                if (currentWebsiteId === websiteId) {
                  // Already on this website, just open the modal
                  openModal()
                } else {
                  // Navigate to the website first, then open the settings modal
                  Turbo.visit(`/sales/websites/${websiteId}`)
                  document.addEventListener('turbo:load', openModal, { once: true })
                }
              }
            },
            {
              id: `website-${index}-details`,
              title: 'Website Details',
              icon: this.websiteIcon(),
              parent: `website-${index}`,
              handler: () => { Turbo.visit(`/sales/websites/${websiteId}`) }
            },
            {
              id: `website-${index}-locations`,
              title: 'Store Locations',
              icon: this.mapIcon(),
              parent: `website-${index}`,
              handler: () => { Turbo.visit(`/sales/locations?website_id=${websiteId}`) }
            },
            {
              id: `website-${index}-chats`,
              title: 'Chats',
              icon: this.chatIcon(),
              parent: `website-${index}`,
              handler: () => { Turbo.visit(`/sales/chats?website_id=${websiteId}`) }
            },
            {
              id: `website-${index}-ai-searches`,
              title: 'AI Searches',
              icon: this.searchIcon(),
              parent: `website-${index}`,
              handler: () => { Turbo.visit(`/sales/ai_search_summaries?website_id=${websiteId}`) }
            },
            {
              id: `website-${index}-product-summaries`,
              title: 'Product Summaries',
              icon: this.documentIcon(),
              parent: `website-${index}`,
              handler: () => { Turbo.visit(`/sales/product_summaries?website_id=${websiteId}`) }
            },
            {
              id: `website-${index}-products`,
              title: 'Products',
              icon: this.boxIcon(),
              parent: `website-${index}`,
              handler: () => { Turbo.visit(`/sales/products?website_id=${websiteId}`) }
            },
            {
              id: `website-${index}-collections`,
              title: 'Collections',
              icon: this.folderIcon(),
              parent: `website-${index}`,
              handler: () => { Turbo.visit(`/sales/collections?website_id=${websiteId}`) }
            },
            {
              id: `website-${index}-knowledge`,
              title: 'Knowledge Base',
              icon: this.bookIcon(),
              parent: `website-${index}`,
              handler: () => { Turbo.visit(`/sales/website_policies?website_id=${websiteId}`) }
            }
          )
        }
      }
    })

    return commands
  }

  getCurrentWebsiteId() {
    // Check URL for website ID
    const match = window.location.pathname.match(/\/sales\/websites\/([^\/]+)/)
    if (match) return match[1]

    // Check URL params
    const params = new URLSearchParams(window.location.search)
    return params.get('website_id')
  }

  // Icon SVGs
  homeIcon() {
    return `<svg class="ninja-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>`
  }

  usersIcon() {
    return `<svg class="ninja-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`
  }

  plusIcon() {
    return `<svg class="ninja-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>`
  }

  settingsIcon() {
    return `<svg class="ninja-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915"/><circle cx="12" cy="12" r="3"/></svg>`
  }

  websiteIcon() {
    return `<svg class="ninja-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect width="18" height="18" x="3" y="3" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>`
  }

  mapIcon() {
    return `<svg class="ninja-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>`
  }

  chatIcon() {
    return `<svg class="ninja-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>`
  }

  searchIcon() {
    return `<svg class="ninja-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>`
  }

  documentIcon() {
    return `<svg class="ninja-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>`
  }

  boxIcon() {
    return `<svg class="ninja-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>`
  }

  folderIcon() {
    return `<svg class="ninja-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/></svg>`
  }

  bookIcon() {
    return `<svg class="ninja-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/></svg>`
  }

  logoutIcon() {
    return `<svg class="ninja-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>`
  }
}
